import React, { useState, useEffect } from 'react';
import {
  FileText,
  Shield,
  Download,
  Plus,
  RefreshCw,
  Search,
  CheckCircle,
  AlertTriangle,
  FileCheck,
  TrendingUp,
  Layers,
  ArrowRight,
  QrCode,
  ExternalLink
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  coreInsuranceApi,
  CorePolicyDto,
  QuoteCalculationRequest,
  QuoteCalculationResult,
  PolicyApportionmentDto
} from '../api/coreInsuranceApi';

export default function CorePolicies() {
  const [policies, setPolicies] = useState<CorePolicyDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Rating & Quote Modal
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [quoteResult, setQuoteResult] = useState<QuoteCalculationResult | null>(null);
  const [quoteForm, setQuoteForm] = useState<QuoteCalculationRequest>({
    productCode: 'MOT-COMP-PRIV',
    coverType: 1, // Comprehensive
    usageCategory: 1, // Private
    sumInsured: 20000000,
    make: 'Toyota',
    model: 'Hilux Double Cabin',
    yearOfManufacture: 2022,
    hasTrackingDevice: true,
    driverAge: 35,
    driverExperienceYears: 10,
    noClaimDiscountYears: 3,
    includeExcessBuyback: true,
    includeWindscreenExtension: true,
    windscreenLimit: 1500000
  });

  // Endorsement Modal
  const [selectedPolicy, setSelectedPolicy] = useState<CorePolicyDto | null>(null);
  const [showEndorsementModal, setShowEndorsementModal] = useState(false);
  const [endorseSumInsured, setEndorseSumInsured] = useState<number>(25000000);
  const [endorsing, setEndorsing] = useState(false);

  // Reinsurance Apportionment Drawer
  const [showCessionModal, setShowCessionModal] = useState(false);
  const [apportionment, setApportionment] = useState<PolicyApportionmentDto | null>(null);
  const [loadingCession, setLoadingCession] = useState(false);

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    setLoading(true);
    try {
      const data = await coreInsuranceApi.getPolicies();
      setPolicies(data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load core policies.');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateQuote = async (e: React.FormEvent) => {
    e.preventDefault();
    setCalculating(true);
    try {
      const res = await coreInsuranceApi.calculateQuote(quoteForm);
      setQuoteResult(res);
      toast.success('Dynamic multi-factor rating completed!');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Rating calculation failed.');
    } finally {
      setCalculating(false);
    }
  };

  const handleBindQuotation = async () => {
    if (!quoteResult) return;
    try {
      const bindCommand = {
        productCode: quoteResult.productCode,
        policyholderName: 'Limbe Leaf Tobacco Ltd',
        policyholderEmail: 'fleet.admin@limbeleaf.mw',
        policyholderPhone: '+265 1 842 111',
        nationalIdNumber: 'LLT-CORP-9881',
        physicalAddress: 'Churchill Road, Limbe, Malawi',
        channel: 'Corporate Broker',
        termMonths: 12,
        sumInsured: quoteResult.sumInsured,
        grossWrittenPremium: quoteResult.grossWrittenPremium,
        netUnderwritingPremium: quoteResult.netUnderwritingPremium,
        registrationNumber: 'BW 4920',
        make: quoteForm.make,
        model: quoteForm.model,
        yearOfManufacture: quoteForm.yearOfManufacture,
        engineNumber: '1GD-FTV-882190',
        chassisNumber: 'MHFJ1283918239',
        coverType: quoteForm.coverType
      };

      await coreInsuranceApi.bindPolicy(bindCommand);
      toast.success('Policy successfully bound! Digital Cover Note & Debit Note generated.');
      setShowQuoteModal(false);
      setQuoteResult(null);
      fetchPolicies();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to bind policy.');
    }
  };

  const handleApplyEndorsement = async () => {
    if (!selectedPolicy) return;
    setEndorsing(true);
    try {
      await coreInsuranceApi.endorsePolicy(selectedPolicy.id, {
        endorsementType: 1, // Financial
        reason: 'Client requested mid-term Sum Insured increase',
        newSumInsured: endorseSumInsured,
        effectiveDate: new Date().toISOString()
      });
      toast.success('Endorsement approved! Pro-rata additional debit note created.');
      setShowEndorsementModal(false);
      fetchPolicies();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Endorsement failed.');
    } finally {
      setEndorsing(false);
    }
  };

  const handleViewCession = async (policy: CorePolicyDto) => {
    setSelectedPolicy(policy);
    setShowCessionModal(true);
    setLoadingCession(true);
    try {
      const data = await coreInsuranceApi.apportionPolicy(policy.id);
      setApportionment(data);
    } catch (err) {
      toast.error('Failed to load reinsurance apportionment.');
    } finally {
      setLoadingCession(false);
    }
  };

  const filtered = policies.filter(p =>
    p.policyNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.policyholderName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.productName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="text-blue-600 dark:text-blue-400" />
            Core Policy Administration (PAS)
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Enterprise underwriting engine, dynamic multi-factor rating, endorsements, and PDF document center.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setQuoteResult(null);
              setShowQuoteModal(true);
            }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg shadow-sm transition"
          >
            <Plus size={18} />
            New Quote & Rating
          </button>
          <button
            onClick={fetchPolicies}
            className="p-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 transition"
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
          <span className="text-xs font-semibold text-gray-400 uppercase">Active In-Force Policies</span>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {policies.filter(p => p.status === 'InForce' || p.status === 6).length}
          </div>
          <span className="text-xs text-green-600 flex items-center gap-1 mt-1 font-medium">
            <CheckCircle size={13} /> Real-time active portfolio
          </span>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
          <span className="text-xs font-semibold text-gray-400 uppercase">Total Sum Insured Book</span>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            MWK {(policies.reduce((acc, p) => acc + (p.totalSumInsured || 0), 0) / 1000000).toFixed(1)}M
          </div>
          <span className="text-xs text-blue-500 font-medium">Covered assets liability</span>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
          <span className="text-xs font-semibold text-gray-400 uppercase">Gross Written Premium</span>
          <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
            MWK {(policies.reduce((acc, p) => acc + (p.grossWrittenPremium || 0), 0) / 1000000).toFixed(2)}M
          </div>
          <span className="text-xs text-gray-400 font-medium">Underwritten revenue</span>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl p-4 shadow-sm">
          <span className="text-xs font-semibold text-gray-400 uppercase">Endorsement Ratio</span>
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
            {policies.filter(p => p.versionNumber > 1).length} MTAs
          </div>
          <span className="text-xs text-purple-500 font-medium">Pro-rata mid-term adjustments</span>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-gray-200 dark:border-slate-800 flex items-center justify-between">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search by policy #, policyholder name, or product..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Policies Table */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-slate-800/60 text-gray-600 dark:text-gray-300 font-semibold border-b border-gray-200 dark:border-slate-800">
              <tr>
                <th className="py-3.5 px-4">Policy Details</th>
                <th className="py-3.5 px-4">Policyholder</th>
                <th className="py-3.5 px-4">Sum Insured</th>
                <th className="py-3.5 px-4">Gross Premium</th>
                <th className="py-3.5 px-4">Status & Ver.</th>
                <th className="py-3.5 px-4 text-right">Official PDF Documents</th>
                <th className="py-3.5 px-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400">
                    <RefreshCw className="animate-spin inline mr-2" size={18} /> Loading core portfolio...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400">
                    No insurance policies found. Click "New Quote & Rating" to bind your first policy.
                  </td>
                </tr>
              ) : (
                filtered.map((policy) => (
                  <tr key={policy.id} className="hover:bg-gray-50/70 dark:hover:bg-slate-800/40 transition">
                    <td className="py-4 px-4">
                      <div className="font-semibold text-blue-600 dark:text-blue-400">{policy.policyNumber}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{policy.productName}</div>
                      {policy.digitalCoverNoteNumber && (
                        <div className="text-xs text-emerald-600 font-mono mt-0.5">CN: {policy.digitalCoverNoteNumber}</div>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="font-medium text-gray-900 dark:text-white">{policy.policyholderName}</div>
                      <div className="text-xs text-gray-400">{policy.channel}</div>
                    </td>
                    <td className="py-4 px-4 font-mono text-gray-900 dark:text-white font-medium">
                      MWK {policy.totalSumInsured?.toLocaleString()}
                    </td>
                    <td className="py-4 px-4 font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                      MWK {policy.grossWrittenPremium?.toLocaleString()}
                    </td>
                    <td className="py-4 px-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                        InForce
                      </span>
                      <div className="text-xs text-gray-400 mt-1">Ver. {policy.versionNumber}.0</div>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => coreInsuranceApi.downloadCoverNotePdf(policy.id, policy.digitalCoverNoteNumber || 'CN')}
                          className="px-2 py-1 bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-900 rounded text-xs flex items-center gap-1 hover:bg-blue-100 transition"
                          title="Download Motor Cover Note PDF with Vector QR Code"
                        >
                          <FileCheck size={13} /> Cover Note
                        </button>
                        <button
                          onClick={() => coreInsuranceApi.downloadSchedulePdf(policy.id, policy.policyNumber)}
                          className="px-2 py-1 bg-gray-50 dark:bg-slate-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-slate-700 rounded text-xs flex items-center gap-1 hover:bg-gray-100 transition"
                          title="Download Policy Schedule PDF"
                        >
                          <FileText size={13} /> Schedule
                        </button>
                        <button
                          onClick={() => coreInsuranceApi.downloadDebitNotePdf(policy.id, policy.policyNumber)}
                          className="px-2 py-1 bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-900 rounded text-xs flex items-center gap-1 hover:bg-amber-100 transition"
                          title="Download Tax Invoice & Debit Note PDF"
                        >
                          <Download size={13} /> Invoice
                        </button>
                        <button
                          onClick={() => coreInsuranceApi.downloadWindshieldDiscPdf(policy.id, policy.riskItems?.[0]?.registrationNumber || policy.policyNumber)}
                          className="px-2 py-1 bg-purple-50 dark:bg-purple-950 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-900 rounded text-xs flex items-center gap-1 hover:bg-purple-100 transition"
                          title="Download Directorate of Road Traffic Compliant Windshield Digital Disc PDF"
                        >
                          <QrCode size={13} /> Disc
                        </button>
                        <a
                          href={`/verify-cover-note/${policy.policyNumber}`}
                          target="_blank"
                          rel="noreferrer"
                          className="px-2 py-1 bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900 rounded text-xs flex items-center gap-1 hover:bg-emerald-100 transition"
                          title="Open Roadside Police Live QR Scan Verification Page"
                        >
                          <ExternalLink size={13} /> Verify
                        </a>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedPolicy(policy);
                            setEndorseSumInsured(policy.totalSumInsured * 1.25);
                            setShowEndorsementModal(true);
                          }}
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium underline"
                        >
                          Endorse (MTA)
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() => handleViewCession(policy)}
                          className="text-xs text-purple-600 hover:text-purple-800 font-medium underline"
                        >
                          Reinsurance
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* RATING & QUOTE MODAL */}
      {showQuoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-2xl w-full border border-gray-200 dark:border-slate-800 shadow-2xl p-6 space-y-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b pb-4 border-gray-200 dark:border-slate-800">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <TrendingUp className="text-blue-600" />
                  Dynamic Multi-Factor Underwriting & Rating Engine
                </h3>
                <p className="text-xs text-gray-500">Computes base rates, NCD discounts, tracker incentives, and statutory levies.</p>
              </div>
              <button
                onClick={() => setShowQuoteModal(false)}
                className="text-gray-400 hover:text-gray-600 font-bold text-xl"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCalculateQuote} className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-1">Make</label>
                <input
                  type="text"
                  value={quoteForm.make}
                  onChange={(e) => setQuoteForm({ ...quoteForm, make: e.target.value })}
                  className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-1">Model</label>
                <input
                  type="text"
                  value={quoteForm.model}
                  onChange={(e) => setQuoteForm({ ...quoteForm, model: e.target.value })}
                  className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-1">Sum Insured (MWK)</label>
                <input
                  type="number"
                  value={quoteForm.sumInsured}
                  onChange={(e) => setQuoteForm({ ...quoteForm, sumInsured: parseFloat(e.target.value) })}
                  className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-1">Year of Manufacture</label>
                <input
                  type="number"
                  value={quoteForm.yearOfManufacture}
                  onChange={(e) => setQuoteForm({ ...quoteForm, yearOfManufacture: parseInt(e.target.value) })}
                  className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-1">No Claim Discount (NCD Years)</label>
                <select
                  value={quoteForm.noClaimDiscountYears}
                  onChange={(e) => setQuoteForm({ ...quoteForm, noClaimDiscountYears: parseInt(e.target.value) })}
                  className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg"
                >
                  <option value={0}>0 Years (0% Discount)</option>
                  <option value={1}>1 Year (15% Discount)</option>
                  <option value={2}>2 Years (25% Discount)</option>
                  <option value={3}>3 Years (35% Discount)</option>
                  <option value={4}>4 Years (45% Discount)</option>
                  <option value={5}>5+ Years (50% Maximum NCD)</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-1">Driver Age</label>
                <input
                  type="number"
                  value={quoteForm.driverAge}
                  onChange={(e) => setQuoteForm({ ...quoteForm, driverAge: parseInt(e.target.value) })}
                  className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg"
                />
              </div>

              <div className="sm:col-span-2 flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={quoteForm.hasTrackingDevice}
                    onChange={(e) => setQuoteForm({ ...quoteForm, hasTrackingDevice: e.target.checked })}
                    className="rounded text-blue-600"
                  />
                  <span>Has GPS Tracking Device (-5% discount)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={quoteForm.includeExcessBuyback}
                    onChange={(e) => setQuoteForm({ ...quoteForm, includeExcessBuyback: e.target.checked })}
                    className="rounded text-blue-600"
                  />
                  <span>Excess Buyback (+1% premium)</span>
                </label>
              </div>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={calculating}
                  className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-sm transition"
                >
                  {calculating ? 'Evaluating Actuarial Matrix...' : 'Compute Dynamic Premium'}
                </button>
              </div>
            </form>

            {/* QUOTE RESULT PANEL */}
            {quoteResult && (
              <div className="bg-blue-50/60 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between border-b border-blue-200 dark:border-blue-900 pb-2">
                  <span className="font-semibold text-blue-900 dark:text-blue-300">Actuarial Quotation Summary</span>
                  <span className="px-2 py-0.5 bg-green-100 text-green-800 font-bold rounded text-xs">Auto-Approved</span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500">Base Premium (4.5%):</span>
                    <div className="font-semibold">MWK {quoteResult.basePremium.toLocaleString()}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">NCD + Tracker Discounts:</span>
                    <div className="font-semibold text-green-600">-MWK {quoteResult.totalDiscounts.toLocaleString()}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Training Levy (1%):</span>
                    <div className="font-semibold">MWK {quoteResult.trainingLevy.toLocaleString()}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">VAT (16.5%):</span>
                    <div className="font-semibold">MWK {quoteResult.valueAddedTax.toLocaleString()}</div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-blue-200 dark:border-blue-900">
                  <div>
                    <div className="text-xs text-gray-500">Total Gross Written Premium</div>
                    <div className="text-xl font-extrabold text-blue-700 dark:text-blue-400">
                      MWK {quoteResult.grossWrittenPremium.toLocaleString()}
                    </div>
                  </div>

                  <button
                    onClick={handleBindQuotation}
                    className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg shadow-md transition flex items-center gap-2"
                  >
                    <CheckCircle size={16} /> Bind Policy & Issue Cover Note
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ENDORSEMENT MODAL */}
      {showEndorsementModal && selectedPolicy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full border border-gray-200 dark:border-slate-800 shadow-2xl p-6 space-y-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Layers className="text-indigo-600" />
              Mid-Term Adjustment (MTA) Endorsement
            </h3>
            <p className="text-xs text-gray-500">
              Policy: <span className="font-mono font-semibold">{selectedPolicy.policyNumber}</span>
            </p>

            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-gray-600 font-medium mb-1 text-xs">Current Sum Insured</label>
                <div className="font-mono font-bold text-gray-900 dark:text-white">
                  MWK {selectedPolicy.totalSumInsured?.toLocaleString()}
                </div>
              </div>

              <div>
                <label className="block text-gray-600 font-medium mb-1 text-xs">New Requested Sum Insured (MWK)</label>
                <input
                  type="number"
                  value={endorseSumInsured}
                  onChange={(e) => setEndorseSumInsured(parseFloat(e.target.value))}
                  className="w-full p-2 bg-gray-50 dark:bg-slate-800 border rounded-lg font-mono font-bold"
                />
              </div>

              <div className="p-3 bg-amber-50 dark:bg-amber-950/40 rounded-lg text-xs text-amber-800 dark:text-amber-300">
                <AlertTriangle size={14} className="inline mr-1" />
                The engine will automatically calculate pro-rata additional premium for the remaining days of the annual term and issue a financial endorsement debit note.
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t">
              <button
                onClick={() => setShowEndorsementModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyEndorsement}
                disabled={endorsing}
                className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg shadow-sm"
              >
                {endorsing ? 'Applying Endorsement...' : 'Confirm & Apply Endorsement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REINSURANCE CESSION DRAWER */}
      {showCessionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-xl w-full border border-gray-200 dark:border-slate-800 shadow-2xl p-6 space-y-4">
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Shield className="text-purple-600" />
                Reinsurance Outward Cession Breakdown
              </h3>
              <button onClick={() => setShowCessionModal(false)} className="text-xl font-bold text-gray-400">
                &times;
              </button>
            </div>

            {loadingCession ? (
              <div className="py-8 text-center text-gray-400">Computing cession shares...</div>
            ) : apportionment ? (
              <div className="space-y-4 text-xs">
                <div className="grid grid-cols-2 gap-3 p-3 bg-purple-50 dark:bg-purple-950/40 rounded-xl border border-purple-200 dark:border-purple-900">
                  <div>
                    <span className="text-gray-500">Primary Insurer Retention:</span>
                    <div className="font-bold text-purple-700 dark:text-purple-300 text-sm">
                      MWK {apportionment.retainedSumInsured?.toLocaleString()} ({((apportionment.retainedPercentage || 0) * 100).toFixed(1)}%)
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Ceded to Reinsurance Treaty:</span>
                    <div className="font-bold text-purple-700 dark:text-purple-300 text-sm">
                      MWK {apportionment.totalCededSumInsured?.toLocaleString()} ({((apportionment.totalCededPercentage || 0) * 100).toFixed(1)}%)
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Total Ceding Commission Earned:</span>
                    <div className="font-bold text-emerald-600 text-sm">
                      MWK {apportionment.totalCedingCommissionEarned?.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Net Treaty Premium Due:</span>
                    <div className="font-bold text-gray-900 dark:text-white text-sm">
                      MWK {apportionment.netReinsurancePremiumPayable?.toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="border-t pt-2">
                  <span className="font-semibold text-gray-700 dark:text-gray-300">Active Cessions ({apportionment.cessions?.length || 0}):</span>
                  <div className="mt-2 space-y-2">
                    {apportionment.cessions?.map((c) => (
                      <div key={c.id || c.cessionNumber} className="p-2 border rounded-lg flex items-center justify-between bg-gray-50 dark:bg-slate-800">
                        <div>
                          <span className="font-bold">{c.cessionNumber}</span>
                          <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded font-semibold text-[10px]">
                            {typeof c.cessionType === 'number' ? (c.cessionType === 3 ? 'Surplus' : 'QuotaShare') : c.cessionType}
                          </span>
                        </div>
                        <div className="text-right">
                          <div>Ceded Sum: MWK {c.cededSumInsured?.toLocaleString()}</div>
                          <div className="text-green-600">Comm: MWK {c.cedingCommissionEarned?.toLocaleString()}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-400 py-4">No cession data available.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
